import { useState, type ReactNode, type Ref } from "react";
import {
  type Control,
  type FieldPath,
  type FieldValues,
  type RegisterOptions,
} from "react-hook-form";
import { Checkbox } from "@/components/ui/checkbox";
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { CUSTOM_ATTRIBUTE_PRESETS } from "./mock-programs";

export function requiredWholeNumber(label: string, min = 0) {
  return {
    required: `${label} is required`,
    validate: (value: string) => {
      const trimmed = value.trim();
      if (!/^\d+$/.test(trimmed)) {
        return `Enter ${label.toLowerCase()} as a whole number`;
      }
      if (Number(trimmed) < min) return `${label} must be at least ${min}`;
      return true;
    },
  };
}

export function optionalWholeNumber(label: string) {
  return {
    validate: (value: string) => {
      const trimmed = value.trim();
      if (trimmed === "") return true;
      if (!/^\d+$/.test(trimmed)) {
        return `Enter ${label.toLowerCase()} as a whole number`;
      }
      return true;
    },
  };
}

export function requiredText(label: string) {
  return {
    required: `${label} is required`,
    validate: (value: string) =>
      value.trim() ? true : `${label} is required`,
  };
}

export function requiredEmail(label: string) {
  return {
    required: `${label} is required`,
    validate: (value: string) => {
      const trimmed = value.trim();
      if (!trimmed) return `${label} is required`;
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)
        ? true
        : `Enter a valid ${label.toLowerCase()}`;
    },
  };
}

export function requiredSlug(label: string) {
  return {
    required: `${label} is required`,
    validate: (value: string) => {
      const trimmed = value.trim();
      if (!trimmed) return `${label} is required`;
      return /^[A-Za-z0-9][A-Za-z0-9_-]{0,63}$/.test(trimmed)
        ? true
        : "Use letters, numbers, hyphens, or underscores";
    },
  };
}

export function requiredWhenMessaging(label: string, readEnabled: () => boolean) {
  return {
    validate: (value: string) => {
      if (!readEnabled()) return true;
      return value.trim()
        ? true
        : `${label} is required when messaging is used`;
    },
  };
}

export function optionalHexColor() {
  return {
    validate: (value: string) => {
      const trimmed = value.trim();
      if (!trimmed) return true;
      return /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(trimmed)
        ? true
        : "Enter a hex colour such as #1B7A45";
    },
  };
}

export function atLeastOne(message: string) {
  return {
    validate: (value: readonly string[]) =>
      value.length > 0 || message,
  };
}

export function expiryMonthsRules(readMode: () => string) {
  return {
    validate: (value: string) => {
      if (readMode() !== "months") return true;
      const trimmed = value.trim();
      if (!/^\d+$/.test(trimmed) || Number(trimmed) < 1) {
        return "Enter expiry in whole months";
      }
      return true;
    },
  };
}

export function parseWholeNumber(value: string): number {
  return Number(value.trim());
}

export function parseOptionalWholeNumber(value: string): number | null {
  const trimmed = value.trim();
  if (!trimmed) return null;
  return Number(trimmed);
}

export function parseCancellationPeriod(value: string): number | null {
  const days = parseOptionalWholeNumber(value);
  if (days == null || days === 0) return null;
  return days;
}

export function HexColorInput({
  value,
  onChange,
  onBlur,
  name,
  inputRef,
  className,
  pickerClassName,
  disabled,
}: {
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  name?: string;
  inputRef?: Ref<HTMLInputElement>;
  className?: string;
  pickerClassName?: string;
  disabled?: boolean;
}) {
  const pickerValue = /^#[0-9a-fA-F]{6}$/.test(value) ? value : "#1B7A45";
  return (
    <div className="flex items-center gap-2">
      <input
        type="color"
        aria-label="Brand colour picker"
        disabled={disabled}
        value={pickerValue}
        onChange={(event) => onChange(event.target.value)}
        className={cn(
          "h-11 w-14 shrink-0 cursor-pointer rounded-xl border border-slate-200 bg-white p-1 disabled:cursor-not-allowed disabled:opacity-60",
          pickerClassName,
        )}
      />
      <Input
        name={name}
        ref={inputRef}
        value={value}
        disabled={disabled}
        placeholder="#1B7A45"
        onBlur={onBlur}
        onChange={(event) => onChange(event.target.value)}
        className={className}
      />
    </div>
  );
}

export function EnumSelectField<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
>({
  control,
  name,
  label,
  options,
  triggerClassName,
  disabled,
  rules,
  hint,
  className,
}: {
  control: Control<TFieldValues>;
  name: TName;
  label: string;
  options: readonly { value: string; label: string }[];
  triggerClassName: string;
  disabled?: boolean;
  rules?: RegisterOptions<TFieldValues, TName>;
  hint?: string;
  className?: string;
}) {
  return (
    <FormField
      control={control}
      name={name}
      rules={rules}
      render={({ field }) => (
        <FormItem className={className}>
          <FormLabel>{label}</FormLabel>
          <Select
            value={String(field.value ?? "")}
            onValueChange={field.onChange}
            disabled={disabled}
          >
            <FormControl>
              <SelectTrigger className={triggerClassName}>
                <SelectValue />
              </SelectTrigger>
            </FormControl>
            <SelectContent>
              {options.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {hint ? (
            <FormDescription className="text-xs text-amber-800/90">
              {hint}
            </FormDescription>
          ) : null}
          <FormMessage />
        </FormItem>
      )}
    />
  );
}

export function MultiCheckField<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
>({
  control,
  name,
  label,
  options,
  rules,
  lockedValues = [],
  hint,
  className,
}: {
  control: Control<TFieldValues>;
  name: TName;
  label: string;
  options: readonly { value: string; label: string }[];
  rules?: RegisterOptions<TFieldValues, TName>;
  lockedValues?: readonly string[];
  hint?: ReactNode;
  className?: string;
}) {
  return (
    <FormField
      control={control}
      name={name}
      rules={rules}
      render={({ field }) => {
        const selected = Array.isArray(field.value)
          ? (field.value as string[])
          : [];
        return (
          <FormItem className={className}>
            <FormLabel>{label}</FormLabel>
            {hint ? (
              <FormDescription className="text-xs">{hint}</FormDescription>
            ) : null}
            <div className="flex flex-wrap gap-2">
              {options.map((option) => {
                const checked = selected.includes(option.value);
                const locked = lockedValues.includes(option.value);
                return (
                  <label
                    key={option.value}
                    className={cn(
                      "flex items-center gap-2 rounded-xl border px-3 py-2 text-sm",
                      checked
                        ? "border-[#1B7A45] bg-emerald-50 text-slate-900"
                        : "border-slate-200 bg-white text-slate-700",
                      locked ? "cursor-not-allowed opacity-80" : "cursor-pointer",
                    )}
                  >
                    <Checkbox
                      checked={checked}
                      disabled={locked}
                      onCheckedChange={(next) => {
                        if (locked) return;
                        if (next === true) {
                          field.onChange([...selected, option.value]);
                          return;
                        }
                        field.onChange(
                          selected.filter((item) => item !== option.value),
                        );
                      }}
                    />
                    {option.label}
                  </label>
                );
              })}
            </div>
            <FormMessage />
          </FormItem>
        );
      }}
    />
  );
}

function attributeKey(value: string) {
  return value
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_")
    .replace(/[^a-z0-9_]/g, "");
}

export function CustomAttributesField<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
>({
  control,
  name,
  lockedValues = [],
  className,
  inputClassName,
}: {
  control: Control<TFieldValues>;
  name: TName;
  lockedValues?: readonly string[];
  className?: string;
  inputClassName?: string;
}) {
  const [draft, setDraft] = useState("");
  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => {
        const selected = Array.isArray(field.value)
          ? (field.value as string[])
          : [];
        const extras = selected.filter(
          (value) =>
            !CUSTOM_ATTRIBUTE_PRESETS.includes(
              value as (typeof CUSTOM_ATTRIBUTE_PRESETS)[number],
            ),
        );
        const write = (next: string[]) => field.onChange(next);
        const addDraft = () => {
          const key = attributeKey(draft);
          if (!key) return;
          if (!selected.includes(key)) write([...selected, key]);
          setDraft("");
        };
        return (
          <FormItem className={className}>
            <FormLabel>Custom attributes</FormLabel>
            <FormDescription className="text-xs">
              Optional. product_type and payment_mode are ready to enable. After
              launch, attributes can be added and cannot be removed.
            </FormDescription>
            <div className="flex flex-wrap gap-2">
              {CUSTOM_ATTRIBUTE_PRESETS.map((option) => {
                const checked = selected.includes(option);
                const locked = lockedValues.includes(option);
                return (
                  <label
                    key={option}
                    className={cn(
                      "flex items-center gap-2 rounded-xl border px-3 py-2 text-sm",
                      checked
                        ? "border-[#1B7A45] bg-emerald-50 text-slate-900"
                        : "border-slate-200 bg-white text-slate-700",
                      locked ? "cursor-not-allowed opacity-80" : "cursor-pointer",
                    )}
                  >
                    <Checkbox
                      checked={checked}
                      disabled={locked}
                      onCheckedChange={(next) => {
                        if (locked) return;
                        if (next === true) {
                          write([...selected, option]);
                          return;
                        }
                        write(selected.filter((item) => item !== option));
                      }}
                    />
                    {option}
                  </label>
                );
              })}
              {extras.map((option) => {
                const locked = lockedValues.includes(option);
                return (
                  <span
                    key={option}
                    className="inline-flex items-center gap-2 rounded-xl border border-[#1B7A45] bg-emerald-50 px-3 py-2 text-sm text-slate-900"
                  >
                    {option}
                    <button
                      type="button"
                      className="text-xs font-semibold text-slate-500 disabled:cursor-not-allowed disabled:opacity-50"
                      disabled={locked}
                      onClick={() =>
                        write(selected.filter((item) => item !== option))
                      }
                    >
                      Remove
                    </button>
                  </span>
                );
              })}
            </div>
            <div className="flex flex-col gap-2 sm:flex-row">
              <Input
                value={draft}
                placeholder="Add another attribute"
                className={inputClassName}
                onChange={(event) => setDraft(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    event.preventDefault();
                    addDraft();
                  }
                }}
              />
              <Button
                type="button"
                variant="outline"
                className="h-11 rounded-xl border-slate-200 bg-white"
                onClick={addDraft}
              >
                Add attribute
              </Button>
            </div>
            <FormMessage />
          </FormItem>
        );
      }}
    />
  );
}
